// src/app/modules/demandes/DemanderFormation.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "../../RessourcesStyles/DemanderFormation.css";
import DemandeFormModal from "./DemandeFormModal";

import {
    Search,
    Filter,
    ChevronDown,
    Eye,
    Download,
    BookOpen,
    Sparkles,
    Layers,
    Calendar,
    X,
} from "lucide-react";

import { fetchVisibleCampagnesRequest } from "../../../../../redux/actions/campagnesActions";
import { fetchCataloguesRequest } from "../../../../../redux/actions/cataloguesActions";
import { fetchFormationsByCatalogueRequest } from "../../../../../redux/actions/formationsActions";
import { createDemandeCollabRequest } from "../../../../../redux/actions/demandesActions";
import HorsCatalogue from "./HorsCatalogue";
import {API_BASE_URL} from "../../../../../../api";

export default function DemanderFormation({
                                              campagneId: campagneIdProp = null,
                                              hideCampagneSelect = false,
                                          }) {
    const dispatch = useDispatch();

    const { visibleList: campagnes = [], loading: campagnesLoading } = useSelector((s) => s.campagnes || {});
    const { catalogues = [], loading: cataloguesLoading } = useSelector((s) => s.catalogues || {});
    const { list: formations = [], loading: formationsLoading } = useSelector((s) => s.formations || {});
    const { currentUser } = useSelector((s) => s.user || {});

    // si pas piloté par parent, fallback local
    const [campagneIdLocal, setCampagneIdLocal] = useState("");
    const campagneId = campagneIdProp ?? (campagneIdLocal ? Number(campagneIdLocal) : null);

    // UI
    const [activeTab, setActiveTab] = useState("catalogues"); // catalogues | horsCatalogue | recommendations
    const [showFilters, setShowFilters] = useState(false);
    const [search, setSearch] = useState("");
    const [catalogueId, setCatalogueId] = useState("all");

    // Pagination formations
    const PAGE_SIZE = 9;
    const [page, setPage] = useState(1);

    // Demande modal
    const [selectedFormation, setSelectedFormation] = useState(null);
    const [openModal, setOpenModal] = useState(false);

    // Details modal (visualisation)
    const [detailsFormation, setDetailsFormation] = useState(null);
    const [openDetails, setOpenDetails] = useState(false);

    useEffect(() => {
        dispatch(fetchVisibleCampagnesRequest());
        dispatch(fetchCataloguesRequest());
    }, [dispatch]);

    // auto select campagne (uniquement si pas de parent)
    useEffect(() => {
        if (campagneIdProp != null) return;
        if (!campagneIdLocal && campagnes?.length) setCampagneIdLocal(String(campagnes[0].id));
    }, [campagnes, campagneIdLocal, campagneIdProp]);

    const selectedCampagne = useMemo(() => {
        if (!campagneId) return null;
        return (campagnes || []).find((c) => String(c.id) === String(campagneId));
    }, [campagnes, campagneId]);

    // catalogues visibles selon campagne.catalogueIds
    const visibleCatalogues = useMemo(() => {
        if (!selectedCampagne?.catalogueIds?.length) return [];
        const setIds = new Set(selectedCampagne.catalogueIds.map((x) => String(x)));
        return (catalogues || []).filter((cat) => setIds.has(String(cat.id)));
    }, [catalogues, selectedCampagne]);

    // reset catalogue si campagne change
    useEffect(() => {
        setCatalogueId("all");
        setActiveTab("catalogues");
        setSearch("");
        setShowFilters(false);
        setPage(1);
    }, [campagneId]);

    // reset catalogue si catalogue plus autorisé
    useEffect(() => {
        if (!campagneId) return;
        if (catalogueId === "all") return;

        const ids = selectedCampagne?.catalogueIds || [];
        const setIds = new Set(ids.map((x) => String(x)));
        if (!setIds.has(String(catalogueId))) setCatalogueId("all");
    }, [campagneId, selectedCampagne, catalogueId]);

    // charger formations via endpoint by catalogue
    useEffect(() => {
        if (!campagneId) return;

        if (catalogueId === "all") {
            dispatch(fetchFormationsByCatalogueRequest("all")); // saga => []
            return;
        }
        dispatch(fetchFormationsByCatalogueRequest(catalogueId));
    }, [dispatch, campagneId, catalogueId]);

    // reset page quand filtre/search change
    useEffect(() => {
        setPage(1);
    }, [search, catalogueId, activeTab]);

    // filtre local uniquement par search (backend filtre déjà par catalogue)
    const filteredFormations = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return formations || [];
        return (formations || []).filter((f) => {
            const name = String(f?.nom || f?.name || "").toLowerCase();
            const ref = String(f?.reference || "").toLowerCase();
            return name.includes(q) || ref.includes(q);
        });
    }, [formations, search]);

    const totalPages = useMemo(() => {
        const n = filteredFormations.length;
        return Math.max(1, Math.ceil(n / PAGE_SIZE));
    }, [filteredFormations.length]);

    const pageSafe = Math.min(Math.max(page, 1), totalPages);
    const pagedFormations = useMemo(() => {
        const start = (pageSafe - 1) * PAGE_SIZE;
        return filteredFormations.slice(start, start + PAGE_SIZE);
    }, [filteredFormations, pageSafe]);

    const selectedCatalogue = useMemo(() => {
        if (catalogueId === "all") return null;
        return (visibleCatalogues || []).find((c) => String(c.id) === String(catalogueId)) || null;
    }, [visibleCatalogues, catalogueId]);

    // 👉 adapte ces clés à ton backend si besoin
    const getCataloguePdfUrl = (cat) =>
        cat?.pdfUrl || cat?.pdfLink || cat?.cataloguePdfUrl || cat?.urlPdf || cat?.lienPdf || cat?.pdf || "";

    const getFormationImage = (f) =>
        f?.imageUrl || f?.image || f?.thumbnail || f?.picture || f?.photo || "";

    const openDemande = (formation) => {
        if (!campagneId) {
            Swal.fire({ icon: "warning", text: "Sélectionne d’abord une campagne." });
            return;
        }
        setSelectedFormation(formation);
        setOpenModal(true);
    };

    const openFormationDetails = (formation) => {
        setDetailsFormation(formation);
        setOpenDetails(true);
    };

    const submitDemande = (payloadFromModal) => {
        if (!campagneId) {
            Swal.fire({ icon: "warning", text: "Campagne obligatoire." });
            return;
        }

        const payload = {
            ...payloadFromModal,
            campagneId,
            formationId: selectedFormation?.id || payloadFromModal.formationId,
            type: "FORMATION",
        };

        dispatch(createDemandeCollabRequest(payload));
        setOpenModal(false);
        setSelectedFormation(null);
    };

    const loading = !!(campagnesLoading || cataloguesLoading || formationsLoading);

    return (
        <div className="dfPage">
            <div className="dfWrap">
                {/*<div className="dfHeader">*/}
                {/*    <h1>Demandes</h1>*/}
                {/*    */}
                {/*</div>*/}

                <div className="dfCard">
                    {/* Step 1: campagne */}
                    <div className="dfStepRow">
                        {!hideCampagneSelect && (
                            <div className="dfField">
                                <label>Campagne *</label>
                                <select value={campagneIdLocal} onChange={(e) => setCampagneIdLocal(e.target.value)}>
                                    <option value="">— Sélectionner —</option>
                                    {(campagnes || []).map((c) => (
                                        <option key={c.id} value={c.id}>{c.nom || c.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {campagneId ? (
                            <div className="dfStepHint">
                                Campagne sélectionnée : <b>{selectedCampagne?.nom || selectedCampagne?.name || "—"}</b>
                            </div>
                        ) : (
                            <div className="dfStepHint">Choisis une campagne pour afficher les onglets.</div>
                        )}
                    </div>

                    {/* Tabs (après campagne) */}
                    {campagneId ? (
                        <div className="dfTabs">
                            <button
                                type="button"
                                className={`dfTab ${activeTab === "catalogues" ? "is-active" : ""}`}
                                onClick={() => setActiveTab("catalogues")}
                            >
                                <Layers size={16} />
                                Catalogues
                            </button>

                            <button
                                type="button"
                                className={`dfTab ${activeTab === "horsCatalogue" ? "is-active" : ""}`}
                                onClick={() => setActiveTab("horsCatalogue")}
                            >
                                <BookOpen size={16} />
                                Hors catalogue
                            </button>

                            <button
                                type="button"
                                className={`dfTab ${activeTab === "recommendations" ? "is-active" : ""}`}
                                onClick={() => setActiveTab("recommendations")}
                            >
                                <Sparkles size={16} />
                                Recommendations
                            </button>
                        </div>
                    ) : null}

                    {/* Content */}
                    {!campagneId ? null : activeTab === "catalogues" ? (
                        <>
                            {/* ✅ TON CODE CATALOGUES + FORMATIONS (inchangé) */}
                            {/* Catalogues cards */}
                            {catalogueId === "all" ? (
                                <>
                                    {loading ? (
                                        <div className="dfEmpty">Chargement...</div>
                                    ) : (visibleCatalogues || []).length === 0 ? (
                                        <div className="dfEmpty">Aucun catalogue disponible pour cette campagne.</div>
                                    ) : (
                                        <div className="dfCatalogueGrid">
                                            {(visibleCatalogues || []).map((cat) => {
                                                const pdfUrl = getCataloguePdfUrl(cat);
                                                return (
                                                    <div className="dfCatalogueCard" key={cat.id}>
                                                        <div className="dfCatalogueTitle">{cat.nom || cat.name || "Catalogue"}</div>
                                                        <div className="dfCatalogueSub">ID: {cat.id}</div>

                                                        <div className="dfCatalogueActions">
                                                            <button
                                                                type="button"
                                                                className="dfIconBtn"
                                                                title="Consulter"
                                                                onClick={() => setCatalogueId(String(cat.id))}
                                                            >
                                                                <Eye size={16} />
                                                            </button>

                                                            <a
                                                                className={`dfIconBtn ${pdfUrl ? "" : "is-disabled"}`}
                                                                title={pdfUrl ? "Télécharger le catalogue (PDF)" : "Lien PDF indisponible"}
                                                                href={pdfUrl || undefined}
                                                                target={pdfUrl ? "_blank" : undefined}
                                                                rel={pdfUrl ? "noreferrer" : undefined}
                                                                onClick={(e) => { if (!pdfUrl) e.preventDefault(); }}
                                                            >
                                                                <Download size={16} />
                                                            </a>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    {/* Formations (scope du moment) */}
                                    <div className="dfFormationsHeader">
                                        <div>
                                            <div className="dfSectionTitle">Formations</div>
                                            <div className="dfSectionSub">
                                                Catalogue : <b>{selectedCatalogue?.nom || selectedCatalogue?.name || catalogueId}</b>
                                            </div>
                                        </div>

                                        <button type="button" className="dfLightBtn" onClick={() => setCatalogueId("all")}>
                                            Retour catalogues
                                        </button>
                                    </div>

                                    <div className="dfControls">
                                        <div className="dfSearch">
                                            <Search size={16} />
                                            <input
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                placeholder="Rechercher une formation (nom, référence...)"
                                            />
                                        </div>

                                        {/*<button type="button" className="dfFilterBtn" onClick={() => setShowFilters((v) => !v)}>*/}
                                        {/*    <Filter size={16} />*/}
                                        {/*    Options*/}
                                        {/*    <ChevronDown size={16} className={`dfChevron ${showFilters ? "is-open" : ""}`} />*/}
                                        {/*</button>*/}
                                    </div>

                                    {showFilters && (
                                        <div className="dfFilters">
                                            <div className="dfField">
                                                <label>Période (info)</label>
                                                <div className="dfInlineInfo">
                                                    <Calendar size={16} />
                                                    <span className="dfMuted">{selectedCampagne?.period || "—"}</span>
                                                </div>
                                            </div>
                                            <div className="dfField">
                                                <label>Réinitialiser</label>
                                                <button type="button" className="dfLightBtn" onClick={() => setSearch("")}>
                                                    Réinitialiser
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {loading ? (
                                        <div className="dfEmpty">Chargement...</div>
                                    ) : filteredFormations.length === 0 ? (
                                        <div className="dfEmpty">Aucune formation trouvée.</div>
                                    ) : (
                                        <>
                                            <div className="dfFormationGrid">
                                                {pagedFormations.map((f) => {
                                                    const img = getFormationImage(f);
                                                    return (
                                                        <div className="dfFormationCard" key={f.id}>
                                                            <div className="dfFormationMedia">
                                                                {f.picture ? (
                                                                    <img
                                                                        className="pdt-thumb"
                                                                        src={`${API_BASE_URL}/api/formations/images/${f.picture}`}
                                                                        alt="domaine"
                                                                        // onError={(e) => (e.currentTarget.style.display = "none")}
                                                                    />
                                                                ) : (
                                                                    <div className="dfFormationPlaceholder">
                                                                        {(f.nom || f.name || "F").slice(0, 1).toUpperCase()}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="dfFormationBody">
                                                                <div className="dfFormationTitle" title={f.nom || f.name || ""}>
                                                                    {f.nom || f.name || "—"}
                                                                </div>

                                                                <div className="dfFormationDesc">
                                                                    {f.description || f.desc || "—"}
                                                                </div>

                                                                <div className="dfFormationMeta">
                                                                    {f.reference ? <span className="dfPill">Ref: {f.reference}</span> : null}
                                                                    {f.duree ? <span className="dfPill">{f.duree} j</span> : null}
                                                                </div>

                                                                <div className="dfFormationActions">
                                                                    <button
                                                                        type="button"
                                                                        className="dfIconBtn"
                                                                        title="Voir les détails"
                                                                        onClick={() => openFormationDetails(f)}
                                                                    >
                                                                        <Eye size={16} />
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="dfPrimaryBtn"
                                                                        onClick={() => openDemande(f)}
                                                                    >
                                                                        Demander
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <div className="dfPagination">
                                                <button
                                                    type="button"
                                                    className="dfLightBtn"
                                                    disabled={pageSafe <= 1}
                                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                                >
                                                    Précédent
                                                </button>

                                                <div className="dfPageInfo">
                                                    Page <b>{pageSafe}</b> / {totalPages} —{" "}
                                                    <span className="dfMuted">{filteredFormations.length} formations</span>
                                                </div>

                                                <button
                                                    type="button"
                                                    className="dfLightBtn"
                                                    disabled={pageSafe >= totalPages}
                                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                                >
                                                    Suivant
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    ) : activeTab === "horsCatalogue" ? (
                        // ✅ Ici tu mets le composant HorsCatalogue
                        <div style={{ marginTop: 10 }}>
                            <HorsCatalogue campagneId={campagneId} hideCampagneSelect />
                        </div>
                    ) : (
                        // recommendations (plus tard)
                        <div className="dfEmpty">⏳ Cet onglet sera traité après. (Recommendations)</div>
                    )}

                </div>
            </div>

            {/* Demande modal */}
            <DemandeFormModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                onSubmit={submitDemande}
                formation={selectedFormation}
                campagneId={campagneId}
                type="FORMATION"
            />

            {/* Modal Details Formation */}
            {openDetails && detailsFormation && (
                <div className="dfModal" onMouseDown={(e) => e.target === e.currentTarget && setOpenDetails(false)}>
                    <div className="dfModal__panel">
                        <div className="dfModal__header">
                            <div className="dfModal__headLeft">
                                <div className="dfModal__icon">
                                    <Eye size={18} />
                                </div>
                                <div>
                                    <div className="dfModal__title">{detailsFormation.nom || detailsFormation.name || "Détails formation"}</div>
                                    <div className="dfModal__sub">ID: {detailsFormation.id}</div>
                                </div>
                            </div>

                            <button type="button" className="dfIconBtn" onClick={() => setOpenDetails(false)} title="Fermer">
                                <X size={16} />
                            </button>
                        </div>

                        <div className="dfModal__body">
                            <div className="dfPanel">
                                <div className="dfPanel__title">Informations</div>

                                <div className="dfGrid2">
                                    <div>
                                        <div className="dfInfo__label">Nom</div>
                                        <div className="dfInfo__value">{detailsFormation.nom || detailsFormation.name || "—"}</div>
                                    </div>

                                    <div>
                                        <div className="dfInfo__label">Référence</div>
                                        <div className="dfInfo__value">{detailsFormation.reference || "—"}</div>
                                    </div>

                                    <div>
                                        <div className="dfInfo__label">Durée</div>
                                        <div className="dfInfo__value">{detailsFormation.duree ? `${detailsFormation.duree} j` : "—"}</div>
                                    </div>

                                    <div>
                                        <div className="dfInfo__label">Catalogue</div>
                                        <div className="dfInfo__value">{selectedCatalogue?.nom || selectedCatalogue?.name || "—"}</div>
                                    </div>
                                </div>

                                <div style={{ marginTop: 12 }}>
                                    <div className="dfInfo__label">Description</div>
                                    <div className="dfInfo__value" style={{ fontWeight: 500, color: "#374151" }}>
                                        {detailsFormation.description || detailsFormation.desc || "—"}
                                    </div>
                                </div>

                                <div className="dfFormationMeta" style={{ marginTop: 10 }}>
                                    {detailsFormation.reference ? <span className="dfPill">Ref: {detailsFormation.reference}</span> : null}
                                    {detailsFormation.duree ? <span className="dfPill">{detailsFormation.duree} j</span> : null}
                                    {detailsFormation.themeNom ? <span className="dfPill">{detailsFormation.themeNom}</span> : null}
                                    {detailsFormation.domaineNom ? <span className="dfPill">{detailsFormation.domaineNom}</span> : null}
                                </div>
                            </div>
                        </div>

                        <div className="dfModal__footer">
                            <button type="button" className="dfLightBtn" onClick={() => setOpenDetails(false)}>
                                Fermer
                            </button>
                            <button
                                type="button"
                                className="dfPrimaryBtn"
                                onClick={() => {
                                    setOpenDetails(false);
                                    openDemande(detailsFormation);
                                }}
                            >
                                Demander
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
